import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../i18n/LanguageContext';
import './Members.css';
import { phoneInputProps, PhoneHint } from '../../utils/phone';

const Members = () => {
    const { user, hasRole, hasPermission } = useAuth();
    const { t } = useLanguage();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0 });
    const [showModal, setShowModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [invitingMember, setInvitingMember] = useState(null);
    const [invitationForm, setInvitationForm] = useState({ email: '', phone: '' });
    const [inviteSending, setInviteSending] = useState(false);
    const [inviteResult, setInviteResult] = useState(null);
    // Change login email/phone of a member who already has a portal account.
    const [contactEdit, setContactEdit] = useState(null);
    const [contactSaving, setContactSaving] = useState(false);
    const [contactError, setContactError] = useState('');
    const [editingMember, setEditingMember] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [activeTab, setActiveTab] = useState('personal'); // Tab state for member edit modal
    const [detailActiveTab, setDetailActiveTab] = useState('personal'); // Tab state for member detail view
    const [profilePictureFile, setProfilePictureFile] = useState(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState(null);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        middle_name: '',
        email: '',
        phone: '',
        gender: '',
        date_of_birth: '',
        national_id: '',
        employer: '',
        employment_number: '',
        department: '',
        job_title: '',
        salary: '',
        branch_id: '',
        membership_status: 'pending',
        membership_date: new Date().toISOString().split('T')[0]
    });

    const params = useParams();
    const navigate = useNavigate();

    // If user is a member and not viewing a specific member detail, redirect to their profile
    useEffect(() => {
        if (!user) return; // Wait for user to load
        
        if (user?.role === 'member' && !params.id) {
            // Load their own member ID and redirect
            api.getMyMember()
                .then(res => {
                    if (res?.data?.member?.id) {
                        navigate(`/members/${res.data.member.id}`, { replace: true });
                    } else {
                        // Fallback: just show loading
                        setLoading(false);
                    }
                })
                .catch(err => {
                    console.error('Failed to load my member profile:', err);
                    setLoading(false);
                });
            return;
        }

        if (params.id) {
            loadMemberDetail(params.id);
        } else {
            loadMembers();
        }
    }, [pagination.page, statusFilter, appliedSearch, params.id, user]);

    // Branches (for dropdowns)
    const [branches, setBranches] = useState([]);
    useEffect(() => {
        const loadBranches = async () => {
            try {
                const res = await api.getBranches({ per_page: 500 });
                setBranches(res.data || []);
            } catch (err) {
                console.error('Failed to load branches', err);
                setBranches([]);
            }
        };
        loadBranches();
    }, []);

    // Search automatically after the user pauses typing.
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setAppliedSearch(search.trim());
            setPagination(prev => prev.page === 1 ? prev : { ...prev, page: 1 });
        }, 350);
        return () => window.clearTimeout(timer);
    }, [search]);

    const [memberDetail, setMemberDetail] = useState(null);
    const [memberStats, setMemberStats] = useState({ contributions: {}, loans: {} });

    const loadMemberDetail = async (id) => {
        setLoading(true);
        try {
            const res = await api.getMember(id);
            // Handle nested response structure
            if (res.data && res.data.member) {
                setMemberDetail(res.data.member);
                setMemberStats({
                    contributions: res.data.contributions || {},
                    loans: res.data.loans || {}
                });
            } else {
                setMemberDetail(res.data || res);
            }
        } catch (error) {
            console.error('Error loading member:', error);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadMembers = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                per_page: pagination.per_page,
                ...(appliedSearch && { search: appliedSearch }),
                ...(statusFilter && { status: statusFilter })
            };
            const response = await api.getMembers(params);
            setMembers(response.data || []);
            if (response.pagination) {
                setPagination(prev => ({ ...prev, ...response.pagination }));
            }
        } catch (error) {
            console.error('Error loading members:', error);
            setMembers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setAppliedSearch(search.trim());
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const changeStatusFilter = (status) => {
        setStatusFilter(status);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleOpenModal = (member = null) => {
        if (member) {
            setEditingMember(member);
            setFormData({
                first_name: member.first_name || '',
                last_name: member.last_name || '',
                middle_name: member.middle_name || '',
                email: member.email || '',
                phone: member.phone || '',
                gender: member.gender || '',
                date_of_birth: member.date_of_birth || '',
                national_id: member.national_id || '',
                employer: member.employer || '',
                employment_number: member.employment_number || '',
                department: member.department || '',
                job_title: member.job_title || '',
                salary: member.salary || '',
                branch_id: member.branch_id || '',
                membership_status: member.membership_status || 'pending',
                membership_date: member.membership_date || new Date().toISOString().split('T')[0]
            });
        } else {
            setEditingMember(null);
            setFormData({
                first_name: '',
                last_name: '',
                middle_name: '',
                email: '',
                phone: '',
                gender: '',
                date_of_birth: '',
                national_id: '',
                employer: '',
                employment_number: '',
                department: '',
                job_title: '',
                salary: '',
                branch_id: '',
                membership_status: 'pending',
                membership_date: new Date().toISOString().split('T')[0]
            });
        }
        setShowModal(true);
    };

    const handleOpenContactModal = async (member) => {
        setContactError('');
        try {
            const res = await api.getMemberAccountContact(member.id);
            setContactEdit({ ...res.data, email: res.data.login_email || '', phone: res.data.phone || '' });
        } catch (err) {
            setContactEdit(null);
            alert(err.message || t('Could not load the account details.'));
        }
    };

    const handleSaveContact = async (e) => {
        e.preventDefault();
        setContactSaving(true);
        setContactError('');
        try {
            const res = await api.updateMemberAccountContact(contactEdit.member_id, { email: contactEdit.email, phone: contactEdit.phone });
            setContactEdit(null);
            if (memberDetail?.id === res.data.member_id) setMemberDetail({ ...memberDetail, email: res.data.member_email, phone: res.data.phone });
            alert(t('Email and phone updated. The member signs in with the new email from now on.'));
        } catch (err) {
            setContactError(t(err.message || 'Could not save the new details.'));
        } finally {
            setContactSaving(false);
        }
    };

    const handleOpenInviteModal = (member) => {
        setInvitingMember(member);
        setInvitationForm({ email: member.email || '', phone: member.phone || '' });
        setShowInviteModal(true);
    };

    const handleCloseInviteModal = () => {
        setShowInviteModal(false);
        setInvitingMember(null);
        setInvitationForm({ email: '', phone: '' });
    };

    const handleInviteChange = (e) => {
        const { name, value } = e.target;
        setInvitationForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSendInvite = async (e) => {
        e.preventDefault();
        if (!invitingMember || inviteSending) return;
        const name = `${invitingMember.first_name || ''} ${invitingMember.last_name || ''}`.trim();
        setInviteSending(true);
        try {
            const res = await api.inviteMember(invitingMember.id, invitationForm);
            const data = res.data || {};
            handleCloseInviteModal();
            setInviteResult({
                ok: !!(data.email_sent || data.sms_sent),
                name,
                link: data.activation_link || null,
                channels: [
                    { label: t('Email'), target: invitationForm.email, sent: !!data.email_sent },
                    { label: 'SMS', target: invitationForm.phone, sent: !!data.sms_sent, note: data.sms_attempted === false ? t('no phone number') : null },
                ],
            });
        } catch (err) {
            console.error('Invite error', err);
            setInviteResult({ ok: false, error: true, name, message: err.message || '' });
        } finally {
            setInviteSending(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingMember(null);
        setActiveTab('personal'); // Reset tab
        setProfilePictureFile(null);
        setProfilePicturePreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingMember) {
                await api.updateMember(editingMember.id, formData);
                
                // Upload photo if selected (after member is updated)
                if (profilePictureFile) {
                    try {
                        await api.uploadMemberPhoto(editingMember.id, profilePictureFile);
                    } catch (photoError) {
                        console.error('Photo upload failed:', photoError);
                        alert(t('Taarifa zimebadilishwa lakini picha haikuweza kupakia'));
                    }
                }
            } else {
                const response = await api.createMember(formData);
                
                // Upload photo if selected (after member is created)
                if (profilePictureFile && response && response.data) {
                    try {
                        await api.uploadMemberPhoto(response.data.id, profilePictureFile);
                    } catch (photoError) {
                        console.error('Photo upload failed:', photoError);
                        alert(t('Mwanajamii ameongezwa lakini picha haikuweza kupakia'));
                    }
                }
            }
            handleCloseModal();
            loadMembers();
        } catch (error) {
            console.error('Error saving member:', error);
            alert(t('Hitilafu imetokea kupakia taarifa'));
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('Are you sure you want to delete this member?'))) {
            try {
                await api.deleteMember(id);
                loadMembers();
            } catch (error) {
                console.error('Error deleting member:', error);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePictureFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePicturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            active: 'badge-success',
            pending: 'badge-warning',
            draft: 'badge-secondary',
            suspended: 'badge-danger',
            retired: 'badge-info',
            deceased: 'badge-secondary',
            resigned: 'badge-warning'
        };
        return statusMap[status] || 'badge-secondary';
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-TZ');
    };

    const getInitials = (firstName, lastName) => {
        const first = firstName ? firstName[0] : '';
        const last = lastName ? lastName[0] : '';
        return (first + last).toUpperCase();
    };

    return (
        <div className="members-page">
            <div className="page-header members-page-header">
                <div className="page-header-left">
                    <span className="members-kicker">TAREWU • MEMBER DIRECTORY</span>
                    <h1 className="page-title">{t('Members')}</h1>
                    <span className="members-count">{pagination.total || 0} {t('total members')}</span>
                </div>
                <div className="page-actions">
                    <div className="view-toggle">
                        <button 
                            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => setViewMode('grid')}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="7" height="7"></rect>
                                <rect x="14" y="3" width="7" height="7"></rect>
                                <rect x="14" y="14" width="7" height="7"></rect>
                                <rect x="3" y="14" width="7" height="7"></rect>
                            </svg>
                        </button>
                        <button 
                            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="8" y1="6" x2="21" y2="6"></line>
                                <line x1="8" y1="12" x2="21" y2="12"></line>
                                <line x1="8" y1="18" x2="21" y2="18"></line>
                                <line x1="3" y1="6" x2="3.01" y2="6"></line>
                                <line x1="3" y1="12" x2="3.01" y2="12"></line>
                                <line x1="3" y1="18" x2="3.01" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    {hasRole(['admin','treasurer','secretary']) && (
                        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            {t('Add Member')}
                        </button>
                    )}
                </div>
            </div>

            {memberDetail && params.id ? (
                <div className="member-profile">
                    <div className="profile-header">
                        <button className="back-btn" onClick={() => navigate('/members')}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="19" y1="12" x2="5" y2="12"></line>
                                <polyline points="12 19 5 12 12 5"></polyline>
                            </svg>
                            {t('Back to Members')}
                        </button>
                    </div>
                    
                    {/* Profile Cover & Header */}
                    <div className="profile-cover">
                        <div className="profile-cover-gradient"></div>
                    </div>
                    
                    <div className="profile-main">
                        <div className="profile-avatar-container">
                            <div className="profile-avatar-large">
                                {getInitials(memberDetail.first_name, memberDetail.last_name)}
                            </div>
                            <span className={`profile-status-badge ${getStatusBadge(memberDetail.membership_status)}`}>
                                {memberDetail.membership_status}
                            </span>
                        </div>
                        
                        <div className="profile-header-info">
                            <h1 className="profile-fullname">{memberDetail.first_name} {memberDetail.last_name}</h1>
                            <p className="profile-member-number">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                                {t('Member #')}{memberDetail.member_number}
                            </p>
                            <div className="profile-meta">
                                <span className="meta-tag">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                                    </svg>
                                    {memberDetail.employer || t('No employer')}
                                </span>
                                <span className="meta-tag">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                        <line x1="16" y1="2" x2="16" y2="6"></line>
                                        <line x1="8" y1="2" x2="8" y2="6"></line>
                                        <line x1="3" y1="10" x2="21" y2="10"></line>
                                    </svg>
                                    {t('Joined')} {formatDate(memberDetail.membership_date)}
                                </span>
                            </div>
                        </div>
                        
                        <div className="profile-actions">
                            {(hasRole(['admin','treasurer','secretary']) || (hasRole('member') && memberDetail?.user_id === user?.id)) && (
                                <button className="btn btn-secondary" onClick={() => handleOpenModal(memberDetail)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                    {t('Edit Profile')}
                                </button>
                            )}

                            {hasPermission('members.update') && memberDetail?.user_id && (
                                <button className="btn btn-secondary" onClick={() => handleOpenContactModal(memberDetail)}>
                                    ✉️ {t('Change email / phone')}
                                </button>
                            )}

                            {hasRole(['admin','treasurer']) && (
                                <button className="btn btn-primary" onClick={() => handleOpenInviteModal(memberDetail)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                        <polyline points="22,6 12,13 2,6"></polyline>
                                    </svg>
                                    {t('Send Invite')}
                                </button>
                            )}
                        </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="profile-stats">
                        <div className="stat-card-mini">
                            <div className="stat-icon-mini">💰</div>
                            <div className="stat-info-mini">
                                <span className="stat-label-mini">{t('Contributions')}</span>
                                <span className="stat-value-mini">
                                    {memberStats.contributions?.total_months || 0} {t('months')}
                                </span>
                            </div>
                        </div>
                        <div className="stat-card-mini">
                            <div className="stat-icon-mini">📈</div>
                            <div className="stat-info-mini">
                                <span className="stat-label-mini">{t('Total Paid')}</span>
                                <span className="stat-value-mini">
                                    {memberStats.contributions?.total_contributions ?
                                        `TZS ${parseInt(memberStats.contributions.total_contributions).toLocaleString()}` :
                                        'TZS 0'}
                                </span>
                            </div>
                        </div>
                        <div className="stat-card-mini">
                            <div className="stat-icon-mini">🏦</div>
                            <div className="stat-info-mini">
                                <span className="stat-label-mini">{t('Active Loans')}</span>
                                <span className="stat-value-mini">
                                    {memberStats.loans?.total_loans || 0}
                                </span>
                            </div>
                        </div>
                        <div className="stat-card-mini">
                            <div className="stat-icon-mini">💳</div>
                            <div className="stat-info-mini">
                                <span className="stat-label-mini">{t('Outstanding')}</span>
                                <span className="stat-value-mini">
                                    {memberStats.loans?.total_balance ? 
                                        `TZS ${parseInt(memberStats.loans.total_balance).toLocaleString()}` : 
                                        'TZS 0'}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Detail Sections - Tabbed Interface */}
                    <div className="profile-content">
                        <div className="profile-tabs-nav">
                            <button 
                                type="button" 
                                className={`tab-button ${detailActiveTab === 'personal' ? 'active' : ''}`}
                                onClick={() => setDetailActiveTab('personal')}
                            >
                                👤 {t('Personal')}
                            </button>
                            <button 
                                type="button" 
                                className={`tab-button ${detailActiveTab === 'employment' ? 'active' : ''}`}
                                onClick={() => setDetailActiveTab('employment')}
                            >
                                💼 {t('Employment')}
                            </button>
                            <button 
                                type="button" 
                                className={`tab-button ${detailActiveTab === 'contact' ? 'active' : ''}`}
                                onClick={() => setDetailActiveTab('contact')}
                            >
                                📞 {t('Contact')}
                            </button>
                        </div>

                        {detailActiveTab === 'personal' && (
                            <div className="profile-section-card tab-content" style={{ animation: 'fadeIn 0.3s ease' }}>
                                <div className="section-header">
                                    <h3 className="section-title">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                        {t('Personal Information')}
                                    </h3>
                                </div>
                                <div className="detail-grid-3">
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Full Name')}</span>
                                        <span className="detail-value">{memberDetail.first_name} {memberDetail.middle_name} {memberDetail.last_name}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Email Address')}</span>
                                        <span className="detail-value">{memberDetail.email || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Phone Number')}</span>
                                        <span className="detail-value">{memberDetail.phone || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Gender')}</span>
                                        <span className="detail-value" style={{ textTransform: 'capitalize' }}>{memberDetail.gender || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Date of Birth')}</span>
                                        <span className="detail-value">{formatDate(memberDetail.date_of_birth)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('National ID')}</span>
                                        <span className="detail-value">{memberDetail.national_id || '-'}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {detailActiveTab === 'employment' && (
                            <div className="profile-section-card tab-content" style={{ animation: 'fadeIn 0.3s ease' }}>
                                <div className="section-header">
                                    <h3 className="section-title">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                        </svg>
                                        {t('Employment Information')}
                                    </h3>
                                </div>
                                <div className="detail-grid-3">
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Employer')}</span>
                                        <span className="detail-value">{memberDetail.employer || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Employment Number')}</span>
                                        <span className="detail-value">{memberDetail.employment_number || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Department')}</span>
                                        <span className="detail-value">{memberDetail.department || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Job Title')}</span>
                                        <span className="detail-value">{memberDetail.job_title || '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Salary Range')}</span>
                                        <span className="detail-value">{memberDetail.salary ? `TZS ${parseInt(memberDetail.salary).toLocaleString()}` : '-'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">{t('Membership Date')}</span>
                                        <span className="detail-value">{formatDate(memberDetail.membership_date)}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {detailActiveTab === 'contact' && (
                            <div className="profile-section-card tab-content" style={{ animation: 'fadeIn 0.3s ease' }}>
                                <div className="section-header">
                                    <h3 className="section-title">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                        </svg>
                                        {t('Contact Information')}
                                    </h3>
                                </div>
                                <div className="contact-grid">
                                    <div className="contact-item">
                                        <div className="contact-icon">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                <polyline points="22,6 12,13 2,6"></polyline>
                                            </svg>
                                        </div>
                                        <div className="contact-info">
                                            <span className="contact-label">{t('Email')}</span>
                                            <span className="contact-value">{memberDetail.email || t('Not provided')}</span>
                                        </div>
                                    </div>
                                    <div className="contact-item">
                                        <div className="contact-icon">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                            </svg>
                                        </div>
                                        <div className="contact-info">
                                            <span className="contact-label">{t('Phone')}</span>
                                            <span className="contact-value">{memberDetail.phone || t('Not provided')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="filters-section">
                        <form className="search-row" onSubmit={handleSearch}>
                            <div className="search-box">
                                <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder={t('Search members...')}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                {search && <button type="button" className="search-clear" aria-label={t('Clear search')} onClick={() => { setSearch(''); setAppliedSearch(''); setPagination(prev => ({ ...prev, page: 1 })); }}>×</button>}
                            </div>
                            <button type="submit" className="btn btn-primary search-submit">
                                {loading ? t('Searching...') : t('Search')}
                            </button>
                        </form>
                        <div className="filter-pills">
                            <button
                                className={`filter-pill ${!statusFilter ? 'active' : ''}`}
                                onClick={() => changeStatusFilter('')}
                            >
                                {t('All')}
                            </button>
                            <button
                                className={`filter-pill ${statusFilter === 'active' ? 'active' : ''}`}
                                onClick={() => changeStatusFilter('active')}
                            >
                                <span className="pill-dot active"></span>
                                {t('Active')}
                            </button>
                            <button
                                className={`filter-pill ${statusFilter === 'suspended' ? 'active' : ''}`}
                                onClick={() => changeStatusFilter('suspended')}
                            >
                                <span className="pill-dot suspended"></span>
                                {t('Suspended')}
                            </button>
                            <button
                                className={`filter-pill ${statusFilter === 'retired' ? 'active' : ''}`}
                                onClick={() => changeStatusFilter('retired')}
                            >
                                <span className="pill-dot retired"></span>
                                {t('Retired')}
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>{t('Loading members...')}</p>
                        </div>
                    ) : members.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                            </div>
                            <h3 className="empty-title">{t('No Members Found')}</h3>
                            <p className="empty-text">{t('Start by adding your first member to the system')}</p>
                            {hasRole(['admin','treasurer','secretary']) && (
                                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                    {t('Add Member')}
                                </button>
                            )}
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="members-grid">
                            {members.map((member) => (
                                <div key={member.id} className="member-card">
                                    <div className="member-card-header">
                                        <div className="member-avatar">
                                            {getInitials(member.first_name, member.last_name)}
                                        </div>
                                        <span className={`badge ${getStatusBadge(member.membership_status)}`}>
                                            {member.membership_status}
                                        </span>
                                    </div>
                                    <div className="member-card-body">
                                        <h3 className="member-name">
                                            <Link to={`/members/${member.id}`}>
                                                {member.first_name} {member.last_name}
                                            </Link>
                                        </h3>
                                        <p className="member-number">{member.member_number}</p>
                                        <p className="member-type">{member.member_type_name || t('Regular Member')}</p>
                                    </div>
                                    <div className="member-card-details">
                                        <div className="member-detail">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                            </svg>
                                            {member.phone || '-'}
                                        </div>
                                        <div className="member-detail">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                                            </svg>
                                            {member.employer || '-'}
                                        </div>
                                        <div className="member-detail">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                            </svg>
                                            {formatDate(member.membership_date)}
                                        </div>
                                    </div>
                                    <div className="member-card-actions">
                                        <Link to={`/members/${member.id}`} className="btn btn-sm btn-secondary">
                                            {t('View')}
                                        </Link>
                                        {hasRole(['admin','treasurer','secretary']) && (
                                            <button className="btn btn-sm btn-secondary" onClick={() => handleOpenModal(member)}>
                                                {t('Edit')}
                                            </button>
                                        )}
                                        {hasRole('admin') && (
                                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(member.id)}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="members-list">
                            {members.map((member) => (
                                <div key={member.id} className="member-list-item">
                                    <div className="member-list-avatar">
                                        {getInitials(member.first_name, member.last_name)}
                                    </div>
                                    <div className="member-list-info">
                                        <div className="member-list-header">
                                            <h3 className="member-name">
                                                <Link to={`/members/${member.id}`}>
                                                    {member.first_name} {member.last_name}
                                                </Link>
                                            </h3>
                                            <span className={`badge ${getStatusBadge(member.membership_status)}`}>
                                                {member.membership_status}
                                            </span>
                                        </div>
                                        <p className="member-meta">
                                            <span className="meta-item">{member.member_number}</span>
                                            <span className="meta-separator">•</span>
                                            <span className="meta-item">{member.employer || t('No employer')}</span>
                                            <span className="meta-separator">•</span>
                                            <span className="meta-item">{member.phone || '-'}</span>
                                        </p>
                                    </div>
                                    <div className="member-list-actions">
                                        <Link to={`/members/${member.id}`} className="action-btn" title={t('View')}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        </Link>
                                        {hasRole(['admin','treasurer','secretary']) && (
                                            <button className="action-btn" title={t('Edit')} onClick={() => handleOpenModal(member)}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                </svg>
                                            </button>
                                        )}
                                        {hasRole('admin') && (
                                            <button className="action-btn danger" title={t('Delete')} onClick={() => handleDelete(member.id)}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {pagination.total_pages > 1 && members.length > 0 && (
                        <div className="pagination">
                            <button
                                className="btn btn-secondary"
                                disabled={pagination.page === 1}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            >
                                {t('Previous')}
                            </button>
                            <span className="pagination-info">
                                {t('Page')} {pagination.page} {t('of')} {pagination.total_pages}
                            </span>
                            <button
                                className="btn btn-secondary"
                                disabled={pagination.page === pagination.total_pages}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            >
                                {t('Next')}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Member Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingMember ? t('Edit Member') : t('New Member')}
                            </h3>
                            <button className="modal-close" onClick={handleCloseModal}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                {/* Tab Navigation */}
                                <div className="profile-tabs-nav">
                                    <button 
                                        type="button"
                                        className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('personal')}
                                    >
                                        👤 {t('Personal')}
                                    </button>
                                    <button 
                                        type="button"
                                        className={`tab-button ${activeTab === 'employment' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('employment')}
                                    >
                                        💼 {t('Employment')}
                                    </button>
                                    <button 
                                        type="button"
                                        className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('contact')}
                                    >
                                        📞 {t('Contact')}
                                    </button>
                                </div>

                                {/* Personal Information Tab */}
                                {activeTab === 'personal' && (
                                    <>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('First Name')} *</label>
                                                <input
                                                    type="text"
                                                    name="first_name"
                                                    className="form-input"
                                                    value={formData.first_name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">{t('Middle Name')}</label>
                                                <input
                                                    type="text"
                                                    name="middle_name"
                                                    className="form-input"
                                                    value={formData.middle_name}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('Last Name')} *</label>
                                                <input
                                                    type="text"
                                                    name="last_name"
                                                    className="form-input"
                                                    value={formData.last_name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">{t('Email')}</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    className="form-input"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('Gender')}</label>
                                                <select
                                                    name="gender"
                                                    className="form-input"
                                                    value={formData.gender}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">{t('Select')}</option>
                                                    <option value="male">{t('Male')}</option>
                                                    <option value="female">{t('Female')}</option>
                                                    <option value="other">{t('Other')}</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">{t('Date of Birth')}</label>
                                                <input
                                                    type="date"
                                                    name="date_of_birth"
                                                    className="form-input"
                                                    value={formData.date_of_birth}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('National ID')}</label>
                                                <input
                                                    type="text"
                                                    name="national_id"
                                                    className="form-input"
                                                    value={formData.national_id}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>

                                        {/* Profile Picture Upload */}
                                        <div className="form-group profile-picture-group">
                                            <label className="form-label">{t('Profile Picture')}</label>
                                            <div className="profile-picture-upload">
                                                {profilePicturePreview && (
                                                    <div className="profile-picture-preview">
                                                        <img src={profilePicturePreview} alt="Profile Preview" />
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => {
                                                                setProfilePictureFile(null);
                                                                setProfilePicturePreview(null);
                                                            }}
                                                            style={{ marginTop: '8px' }}
                                                        >
                                                            {t('Remove')}
                                                        </button>
                                                    </div>
                                                )}
                                                <input
                                                    type="file"
                                                    id="profile-picture-input"
                                                    accept="image/*"
                                                    onChange={handleProfilePictureChange}
                                                    style={{ display: 'none' }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => document.getElementById('profile-picture-input').click()}
                                                >
                                                    {profilePictureFile ? `📷 ${t('Change Picture')}` : `📷 ${t('Upload Picture')}`}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Employment Information Tab */}
                                {activeTab === 'employment' && (
                                    <>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('Employer')}</label>
                                                <input
                                                    type="text"
                                                    name="employer"
                                                    className="form-input"
                                                    value={formData.employer}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">{t('Employment #')}</label>
                                                <input
                                                    type="text"
                                                    name="employment_number"
                                                    className="form-input"
                                                    value={formData.employment_number}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('Department')}</label>
                                                <input
                                                    type="text"
                                                    name="department"
                                                    className="form-input"
                                                    value={formData.department}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">{t('Job Title')}</label>
                                                <input
                                                    type="text"
                                                    name="job_title"
                                                    className="form-input"
                                                    value={formData.job_title}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('Salary')}</label>
                                                <input
                                                    type="number"
                                                    name="salary"
                                                    className="form-input"
                                                    value={formData.salary}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Contact Information Tab */}
                                {activeTab === 'contact' && (
                                    <>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('Phone')} *</label>
                                                <input
                                                    {...phoneInputProps}
                                                    name="phone"
                                                    className="form-input"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <PhoneHint value={formData.phone} t={t} />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('Branch')}</label>
                                                <select
                                                    name="branch_id"
                                                    className="form-input"
                                                    value={formData.branch_id}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">{t('Select branch')}</option>
                                                    {branches.map(b => (
                                                        <option key={b.id} value={b.id}>{b.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">{t('Membership Status')}</label>
                                                <input className="form-input" value={editingMember?.membership_status || t('Pending profile completion')} disabled />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">{t('Membership Date')}</label>
                                                <input
                                                    type="date"
                                                    name="membership_date"
                                                    className="form-input"
                                                    value={formData.membership_date}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                                    {t('Cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingMember ? t('Update Member') : t('Create Member')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Change login email / phone (members with a portal account) */}
            {contactEdit && (
                <div className="modal-overlay" onClick={() => setContactEdit(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('Change email / phone')}</h3>
                            <button className="modal-close" onClick={() => setContactEdit(null)} aria-label={t('Close')}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                        </div>
                        <form onSubmit={handleSaveContact}>
                            <div className="modal-body">
                                <p className="invite-info">
                                    <strong>{contactEdit.name}</strong> · {t('account')}: {contactEdit.account_status === 'active' ? t('active') : t('not yet activated')}
                                </p>
                                {contactError && <div className="error-message">{contactError}</div>}
                                <div className="form-group">
                                    <label className="form-label">{t('Email (used to sign in)')}</label>
                                    <input type="email" className="form-input" value={contactEdit.email} onChange={(e) => setContactEdit({ ...contactEdit, email: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('Phone')}</label>
                                    <input {...phoneInputProps} className="form-input" value={contactEdit.phone} onChange={(e) => setContactEdit({ ...contactEdit, phone: e.target.value })} required />
                                    <PhoneHint value={contactEdit.phone} t={t} />
                                </div>
                                <p className="invite-info">{t('Sign-in, password reset links and SMS will use the new details. If the account is not yet activated, send the invitation again afterwards.')}</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setContactEdit(null)}>{t('Cancel')}</button>
                                <button type="submit" className="btn btn-primary" disabled={contactSaving}>{contactSaving ? t('Saving...') : t('Save')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="modal-overlay" onClick={handleCloseInviteModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('Send Invitation')}</h3>
                            <button className="modal-close" onClick={handleCloseInviteModal}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleSendInvite}>
                            <div className="modal-body">
                                <p className="invite-info">
                                    {t('Send invitation to')} <strong>{invitingMember?.first_name} {invitingMember?.last_name}</strong>
                                </p>
                                <div className="form-group">
                                    <label className="form-label">{t('Email')}</label>
                                    <input
                                        type="email"
                                        name="email"
                                        className="form-input"
                                        value={invitationForm.email}
                                        onChange={handleInviteChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('Phone')}</label>
                                    <input
                                        {...phoneInputProps}
                                        name="phone"
                                        className="form-input"
                                        value={invitationForm.phone}
                                        onChange={handleInviteChange}
                                        required
                                    />
                                    <PhoneHint value={invitationForm.phone} t={t} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseInviteModal}>{t('Cancel')}</button>
                                <button type="submit" className="btn btn-primary" disabled={inviteSending}>{inviteSending ? t('Sending...') : t('Send Invite')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Invite Result */}
            {inviteResult && (
                <div className="modal-overlay" onClick={() => setInviteResult(null)}>
                    <div className="modal invite-result" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-live="polite">
                        <div className={`invite-result-icon ${inviteResult.ok ? 'ok' : inviteResult.error ? 'error' : 'warn'}`} aria-hidden="true">
                            {inviteResult.ok ? '✓' : inviteResult.error ? '✕' : '!'}
                        </div>
                        <h3>{inviteResult.error ? t('Failed to send invite') : inviteResult.ok ? t('Invitation sent') : t('Invitation not delivered')}</h3>
                        {inviteResult.name && <p className="invite-result-name">{inviteResult.name}</p>}
                        {inviteResult.error ? (
                            <p className="invite-result-message">{inviteResult.message}</p>
                        ) : (
                            <ul className="invite-result-channels">
                                {inviteResult.channels.map((channel) => (
                                    <li key={channel.label} className={channel.sent ? 'sent' : 'failed'}>
                                        <span className="channel-mark" aria-hidden="true">{channel.sent ? '✓' : '✕'}</span>
                                        <span className="channel-text"><b>{channel.label}</b><small>{channel.note || channel.target || '—'}</small></span>
                                        <span className="channel-status">{channel.sent ? t('Sent') : t('Not sent')}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {!inviteResult.error && !inviteResult.ok && <p className="invite-result-hint">{t('Check the email address and phone number, then try again. Details are in SMS Logs and System Logs.')}</p>}
                        {inviteResult.link && <p className="invite-result-link"><small>{t('Activation link:')}</small><code>{inviteResult.link}</code></p>}
                        <button type="button" className="btn btn-primary" onClick={() => setInviteResult(null)}>{t('Close')}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Members;
